import { useState, useCallback, useRef, useEffect } from 'react';

interface UseSearchOptions<T> {
  fetchFunction: (params: { search?: string; skip?: number; limit?: number; active_only?: boolean }) => Promise<T[]>;
  debounceMs?: number;
  minSearchLength?: number;
}

interface UseSearchReturn<T> {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  results: T[];
  loading: boolean;
  error: string;
  performSearch: (query: string) => Promise<void>;
  clearSearch: () => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleClearSearch: () => void;
}

/**
 * Reusable search hook for list pages
 * Provides debounced search, loading states, and error handling
 */
export function useSearch<T>({
  fetchFunction,
  debounceMs = 800,
  minSearchLength = 1,
}: UseSearchOptions<T>): UseSearchReturn<T> {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitialLoadRef = useRef(false);

  // Memoize the fetch function to prevent infinite re-renders
  const memoizedFetchFunction = useCallback(async (params: { search?: string; skip?: number; limit?: number; active_only?: boolean }) => {
    return await fetchFunction(params);
  }, [fetchFunction]);

  const fetchResults = useCallback(async (search?: string) => {
    try {
      setLoading(true);
      setError('');

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      const response = await memoizedFetchFunction({
        search: search || undefined,
        limit: 100,
        active_only: true,
      });

      // Only update state if this request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setResults(response);
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('useSearch: Error:', err);
        setError(`Failed to ${search ? 'search' : 'load'} items`);
        console.error('Search error:', err);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [memoizedFetchFunction]);

  const performSearch = useCallback(async (query: string) => {
    await fetchResults(query);
  }, [fetchResults]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setError('');
    // Fetch all items when search is cleared
    fetchResults();
  }, [fetchResults]);

  // Debounced search effect - only depends on searchTerm
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    if (searchTerm.length >= minSearchLength) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, debounceMs);
    } else if (searchTerm.length === 0) {
      // Clear search results when search is empty
      fetchResults();
    }

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, debounceMs, minSearchLength, performSearch, fetchResults]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    // If user clears search, immediately fetch all items
    if (value.length === 0) {
      fetchResults();
    }
  }, [fetchResults]);

  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchTerm.length >= minSearchLength) {
      event.preventDefault();
      performSearch(searchTerm);
    }
  }, [searchTerm, performSearch, minSearchLength]);

  const handleClearSearch = useCallback(() => {
    clearSearch();
  }, [clearSearch]);

  // Initial load - only run once
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      fetchResults();
    }
  }, [fetchResults]); // Empty dependency array - only run once

  return {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    error,
    performSearch,
    clearSearch,
    handleSearchChange,
    handleSearchKeyDown,
    handleClearSearch,
  };
}
